import React, { useEffect } from "react";
import { OrderItem } from "../types";
import { fetchWithAuth } from "../util/fetchWithAuth";

type OrderCostModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orderItems: OrderItem[];
  onConfirm: (updatedItems: OrderItem[]) => void;
  orderId?: number;
};

const OrderCostModal: React.FC<OrderCostModalProps> = ({ isOpen, orderItems, onConfirm, onClose }) => {
  const [items, setItems] = React.useState(orderItems);

  useEffect(() => {
    setItems(orderItems);
  }, [orderItems]);

  const updateCost = (index: number, cost: number) => {
    const newItems = [...items];
    newItems[index].cost = cost;
    setItems(newItems);
  };

  const handleConfirm = async () => {
    try {
      const response = await fetchWithAuth(`/order_items/bulk_update_purchase_costs`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          order_items: items.map((item) => ({
            id: item.id,
            cost: item.cost,
          })),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Failed to update purchase costs:", errorData.error);
        alert(`Error: ${errorData.error}`);
        return;
      }

      const updatedItems = await response.json();
      onConfirm(updatedItems);
      onClose();
    } catch (error) {
      console.error("An error occurred while updating purchase costs:", error);
      alert("An unexpected error occurred. Please try again.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="max-h-[80vh] overflow-y-auto w-full max-w-2xl">
        <div className="bg-white p-6 rounded-md shadow-lg w-full">
          <h2 className="text-lg font-bold mb-4 text-center">Order Item Costs</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 items-center font-bold text-sm border-b pb-2">
            <span className="text-left">Store</span>
            <span className="text-right">Price</span>
            <span className="text-center">Cost</span>
            </div>
            {(() => {
              // group items by store id
              const groupedItems = items.reduce((acc, item) => {
                const storeId = item.store.id;
                if (!acc[storeId]) acc[storeId] = [];
                acc[storeId].push(item);
                return acc;
              }, {} as Record<number, OrderItem[]>);
              return Object.entries(groupedItems).map(([storeId, group]) => {
                const totalPrice = group.reduce((sum, i) => sum + Number(i.total_price), 0);
                const totalCost = group.reduce((sum, i) => sum + Number(i.cost || 0), 0);
                return (
                  <div key={storeId} className="space-y-2 bg-gray-50 p-4 rounded-md shadow-sm">
                    <h3 className="text-sm font-semibold mb-2">{group[0].store.name}</h3>
                    {group.map((item) => (
                      <div key={item.id} className="grid grid-cols-3 gap-2 items-center even:bg-white odd:bg-gray-100 py-1 rounded">
                        <span className="text-sm w-full block break-words">{item.description}</span>
                        <span className="text-sm font-medium text-right w-full block">E{Number(item.total_price).toFixed(2)}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={item.cost || 0}
                          onChange={(e) => updateCost(items.indexOf(item), Number(e.target.value))}
                          className="border rounded px-2 py-1 text-sm text-right w-full"
                          placeholder="Cost"
                        />
                      </div>
                    ))}
                    <div className="grid grid-cols-3 gap-2 items-center font-semibold border-t pt-2 bg-yellow-100 rounded">
                      <span className="text-sm italic text-gray-600 text-left">Total</span>
                      <span className="text-sm text-right">E{totalPrice.toFixed(2)}</span>
                      <span className="text-sm text-right">E{totalCost.toFixed(2)}</span>
                    </div>
                  </div>
                );
              });
            })()}
        </div>
          <div className="flex justify-end gap-4 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderCostModal;
